/**
 * SF-4940: batch actions on the Companies list. Set one account type on many companies, and
 * schedule many Demo or Test companies for deletion, one request per company. Account type uses
 * the same PATCH as the company page. Deletion does NOT use the danger zone's DELETE: it uses
 * sf-api's schedule-only route, which checks the type in the claim and does not exist on older
 * servers (whose DELETE purges at once).
 */
import type { DataProvider } from '@refinedev/core';
import { runBatch, type BatchResult } from '../../utils/batch';
import { COMPANIES_API_BASE, type CompanyDeleteImpact } from './api/companyDeleteClient';
import { canEditCompanyId } from './api/editableCompaniesClient';
import {
  ACCOUNT_TYPES,
  ACCOUNT_TYPE_LABELS,
  COMPANIES_META,
  INTERNAL_ACCOUNT_TYPE,
  type AccountType,
} from './constants';
import type { Company } from './list';

/** Only these account types can be deleted in a batch (Alan, SF-4940). */
export const BATCH_DELETABLE_TYPES: readonly AccountType[] = ['Demo', 'Test'];

/**
 * Types a batch can set. Not ServeFirst Live (SF-5100): only one company may hold it, so it is
 * set on that company's own page.
 */
export const BATCH_SETTABLE_TYPES: readonly AccountType[] = ACCOUNT_TYPES.filter(
  (t) => t !== INTERNAL_ACCOUNT_TYPE,
);

export const companyId = (c: Company): string => c._id || c.id || '';

/**
 * Why a company can't be ticked, or null when it can. Paid companies are never in a batch;
 * a company already on its way out is handled from Deleted companies; and staff limited to
 * some companies can only tick those (`editScope` is the settled sf-api answer, null = unknown).
 */
export function batchTickBlock(company: Company, editScope: unknown): string | null {
  if (company.deletion_status === 'deleting') return 'Being deleted';
  if (company.deletion_status) return 'Waiting for deletion';
  // No stored type reads as Paid on the server (the schema default).
  if (!company.account_type || company.account_type === 'Paid') {
    return 'Paid companies are changed one at a time';
  }
  // SF-5100: ServeFirst's own working company is never in a batch, so a batch can't turn it into
  // Demo or Test and then delete it.
  if (company.account_type === INTERNAL_ACCOUNT_TYPE) {
    return `${ACCOUNT_TYPE_LABELS[INTERNAL_ACCOUNT_TYPE]} is changed on its own page`;
  }
  if (!canEditCompanyId(editScope, companyId(company))) return 'Not on your list of companies';
  return null;
}

/** True when every company may be scheduled for deletion together. */
export const allBatchDeletable = (companies: Company[]): boolean =>
  companies.length > 0 &&
  companies.every((c) => !!c.account_type && BATCH_DELETABLE_TYPES.includes(c.account_type));

/**
 * sf-api answers `{ success: false, message }`. A 5xx never shows the server's own text, which
 * may be a raw exception (same rule as the danger zone). With no reply at all, the error's own
 * text is shown (a network error, or one of this file's own refusals).
 */
export function companyBatchErrorMessage(err: unknown): string {
  const e = err as {
    response?: { status?: number; data?: { message?: unknown } };
    message?: unknown;
  };
  const status = e?.response?.status;
  if (status !== undefined) {
    const serverMessage = e.response?.data?.message;
    if (status >= 500) return 'Something went wrong. Please try again.';
    if (status === 403) return 'You are not allowed to change this company.';
    // An sf-api from before batch deletion has no schedule route (Express's own 404, no message).
    if (status === 404 && !(typeof serverMessage === 'string' && serverMessage.trim())) {
      return 'This server cannot schedule deletions in a batch yet. Use the company page.';
    }
    return typeof serverMessage === 'string' && serverMessage.trim()
      ? serverMessage
      : `Request failed (${status})`;
  }
  return typeof e?.message === 'string' && e.message.trim() ? e.message : 'Unknown error';
}

type Provider = DataProvider;

/**
 * The server copy of a company, refused (thrown) when a batch must not touch it any more:
 * it became Paid, or it is on its way out.
 */
async function readBatchable(provider: Provider, id: string): Promise<Company> {
  const { data: company } = await provider.getOne<Company>({
    resource: 'companies',
    id,
    meta: COMPANIES_META,
  });
  if (!company) throw new Error('Company not found');
  if (company.deletion_status) throw new Error('Already waiting for deletion');
  if (!company.account_type || company.account_type === 'Paid') {
    throw new Error('It is Paid now, so it can only be changed on its own page');
  }
  if (company.account_type === INTERNAL_ACCOUNT_TYPE) {
    throw new Error(
      `It is ${ACCOUNT_TYPE_LABELS[INTERNAL_ACCOUNT_TYPE]} now, so it can only be changed on its own page`,
    );
  }
  return company;
}

/** sf-api's schedule-only deletion route (SF-4940), used by the batch. */
export const scheduleDeletionUrl = (id: string) => `/${COMPANIES_API_BASE}/${id}/deletion/schedule`;

/** The company's delete-impact answer (counts, waiting period, open deletion). */
export async function fetchDeleteImpact(
  provider: Provider,
  id: string,
): Promise<CompanyDeleteImpact | undefined> {
  const { data } = await provider.custom!<CompanyDeleteImpact>({
    url: `/${COMPANIES_API_BASE}/${id}/delete-impact`,
    method: 'get',
  });
  return data;
}

/** True when this sf-api schedules deletions (a waiting period of at least a day). */
export const schedulesDeletion = (
  impact: CompanyDeleteImpact | undefined,
): impact is CompanyDeleteImpact & { graceDays: number } =>
  typeof impact?.graceDays === 'number' && impact.graceDays >= 1;

/** Sets `accountType` on every company in `ids`. */
export const setAccountTypeForCompanies = (
  provider: Provider,
  ids: string[],
  accountType: AccountType,
): Promise<BatchResult> =>
  runBatch(
    ids,
    async (id) => {
      // The tick may be old (kept across pages): check the server copy is still allowed.
      await readBatchable(provider, id);
      await provider.update({
        resource: 'companies',
        id,
        variables: { account_type: accountType },
        meta: COMPANIES_META,
      });
      return 'updated';
    },
    undefined,
    companyBatchErrorMessage,
  );

/**
 * Schedules the deletion of every company in `ids`, each checked again on the server copy
 * first: still Demo or Test, not already on its way out, and the server SCHEDULES deletions
 * (its delete-impact answer has a waiting period). An sf-api without scheduled deletion
 * deletes at once, so a company is never sent there from a batch. The request goes to the
 * schedule-only route and names the allowed types, so sf-api refuses one whose type changed
 * after these reads.
 */
export const scheduleDeletionForCompanies = (
  provider: Provider,
  ids: string[],
): Promise<BatchResult> =>
  runBatch(
    ids,
    async (id) => {
      const company = await readBatchable(provider, id);
      if (!company.account_type || !BATCH_DELETABLE_TYPES.includes(company.account_type)) {
        throw new Error('Only Demo and Test companies can be deleted together');
      }
      const impact = await fetchDeleteImpact(provider, id);
      if (!schedulesDeletion(impact)) {
        throw new Error('This server deletes at once, so it can only be deleted from its own page');
      }
      if (impact.deletionStatus || impact.deletion) {
        throw new Error('Already waiting for deletion');
      }
      // The schedule-only route, never DELETE: a server from before scheduled deletion has no
      // such route (404), where its DELETE would purge at once. `accountTypes` makes sf-api check
      // the type again in the write that schedules it, so a company made Paid a moment ago is
      // refused (409), not deleted.
      await provider.custom!({
        url: scheduleDeletionUrl(id),
        method: 'post',
        payload: {
          confirmName: impact.companyName ?? company.name,
          accountTypes: [...BATCH_DELETABLE_TYPES],
        },
      });
      return 'updated';
    },
    undefined,
    companyBatchErrorMessage,
  );
